import { MouseEvent, useMemo, useState } from 'react';

import { useRouter } from 'next/router';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

import { IconType } from 'react-icons';
import { CreateToastFnReturn, Tooltip, useToast } from '@chakra-ui/react';

import { validateUserPermissions } from '~/shared/utils/validateUserPermissions';
import { FCWithChildren } from '~/shared/types/FCWithChildren';
import { Spinner } from '~/shared/components/Spinner';
import { Badge } from '~/shared/components/Badge';
import { useAuth } from '~/modules/auth/hooks/auth';

import { Container } from './styles';

interface NavItemProps extends NextLinkProps {
  icon: IconType;
  permissions?: string[];
  shouldMatchExactHref?: boolean;
  isDisabled?: boolean;
  comingSoon?: boolean;
  isNew?: boolean;
  id?: string;
  hint?: string;
  onClickAction?: (toast?: CreateToastFnReturn) => void | Promise<void>;
}

export const NavItem: FCWithChildren<NavItemProps> = ({
  icon: Icon,
  children,
  permissions,
  shouldMatchExactHref = false,
  isDisabled = false,
  comingSoon = false,
  isNew = false,
  href,
  as,
  id,
  hint,
  onClickAction,
  ...props
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { asPath } = useRouter();

  const toast = useToast({
    position: 'top-right',
    duration: 2500,
  });

  const isActive = useMemo(() => {
    if (shouldMatchExactHref && (asPath === href || asPath === as)) {
      return true;
    }

    if (
      !shouldMatchExactHref &&
      (asPath.startsWith(String(href)) || asPath.startsWith(String(as)))
    ) {
      return true;
    }

    return false;
  }, [asPath, href, as, shouldMatchExactHref]);

  const userHasValidPermissions = validateUserPermissions({
    user,
    permissions,
  });

  if (!userHasValidPermissions) return null;

  const handleClick = async (
    event: MouseEvent<HTMLAnchorElement>
  ): Promise<void> => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }

    if (onClickAction) {
      event.preventDefault();

      try {
        setIsLoading(true);
        await onClickAction(toast);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Container isActive={isActive} isDisabled={isDisabled || isLoading} id={id}>
      <Tooltip label={hint} hasArrow isDisabled={!hint}>
        <NextLink href={href || '#'} passHref onClick={handleClick} {...props}>
          <Icon />

          <span>{children}</span>

          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              {comingSoon && <Badge type="primary">breve</Badge>}
              {isNew && <Badge type="primary">novo</Badge>}
            </>
          )}
        </NextLink>
      </Tooltip>
    </Container>
  );
};
